import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JourneyService, { normalizeJourneyProblemKey } from "./services/JourneyService";
import { getProblemByKey, getProblemTitleForLang } from "./journeyProblems";
import { getBlueprintForProblem } from "./JourneyBlueprints";
import { dirForLang, normalizeLang } from "./i18n";
import { API_BASE } from "./apiConfig";
import { getSystemStrings } from "./strings/systemStrings";
import { useAuth } from "./auth/AuthContext";
import "./Intake.css";

function useQueryParams() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const lang = normalizeLang(params.get("lang") || "he");
  const problemKey = String(params.get("problemKey") || "").trim();
  return { lang, problemKey, params, location };
}

function isReactNativeWebView() {
  return typeof window !== "undefined" && window.ReactNativeWebView != null;
}

function isLikelyIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/i.test(navigator.userAgent || "");
}

function mapSpeechErrorMessage(code, labels) {
  const c = String(code || "");
  if (c === "not-allowed" || c === "service-not-allowed") return labels.micErrorBlocked;
  if (c === "network") return labels.micErrorNetwork;
  return labels.micErrorGeneric;
}

function SpeechButton({ lang, onText, labels }) {
  const [supported, setSupported] = useState(true);
  const [listening, setListening] = useState(false);
  const [errorHint, setErrorHint] = useState("");
  const recognitionRef = useRef(null);
  const stopRequested = useRef(false);
  const isStartingRef = useRef(false);
  const isListeningRef = useRef(false);
  const restartTimerRef = useRef(null);
  const latestOnTextRef = useRef(onText);
  const labelsRef = useRef(labels);
  const errorClearRef = useRef(null);

  useEffect(() => {
    latestOnTextRef.current = onText;
  }, [onText]);

  useEffect(() => {
    labelsRef.current = labels;
  }, [labels]);

  const clearRestartTimer = () => {
    if (restartTimerRef.current) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  };

  const startRecognition = () => {
    const rec = recognitionRef.current;
    if (!rec || isStartingRef.current || isListeningRef.current) return;
    isStartingRef.current = true;
    setErrorHint("");
    try {
      rec.start();
    } catch {
      isStartingRef.current = false;
      isListeningRef.current = false;
      setListening(false);
      setErrorHint(labelsRef.current.micErrorGeneric);
    }
  };

  const stopRecognition = () => {
    clearRestartTimer();
    stopRequested.current = true;
    isStartingRef.current = false;
    isListeningRef.current = false;
    try {
      recognitionRef.current?.stop();
    } catch {
      // Ignore invalid stop calls.
    }
    setListening(false);
  };

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const inApp = isReactNativeWebView();
    const ios = isLikelyIOS();
    /* WebView באייפון/אייפד: Web Speech API בפועל לא נתמך או נכשל בשקט — לא מציגים כפתור "חי" שלא עושה כלום */
    if (!SR || (inApp && ios)) {
      setSupported(false);
      return;
    }
    const rec = new SR();
    rec.lang = lang === "he" ? "he-IL" : lang === "es" ? "es-ES" : "en-US";
    rec.continuous = true;
    rec.interimResults = false;
    rec.onstart = () => {
      isStartingRef.current = false;
      isListeningRef.current = true;
      setListening(true);
      setErrorHint("");
    };
    rec.onresult = (e) => {
      let transcript = "";
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const piece = e.results[i]?.[0]?.transcript;
        if (piece) transcript += piece;
      }
      transcript = transcript.trim();
      if (transcript && latestOnTextRef.current) {
        latestOnTextRef.current(transcript);
      }
    };
    rec.onend = () => {
      isStartingRef.current = false;
      isListeningRef.current = false;
      if (!stopRequested.current && recognitionRef.current === rec) {
        clearRestartTimer();
        restartTimerRef.current = window.setTimeout(() => {
          startRecognition();
        }, 150);
      } else {
        setListening(false);
      }
    };
    rec.onerror = (ev) => {
      clearRestartTimer();
      isStartingRef.current = false;
      isListeningRef.current = false;
      setListening(false);
      const code = ev?.error || "";
      stopRequested.current =
        code === "not-allowed" || code === "service-not-allowed" || code === "aborted";
      const msg = mapSpeechErrorMessage(code, labelsRef.current);
      setErrorHint(msg);
      if (errorClearRef.current) window.clearTimeout(errorClearRef.current);
      errorClearRef.current = window.setTimeout(() => setErrorHint(""), 8000);
    };
    recognitionRef.current = rec;
    return () => {
      stopRequested.current = true;
      clearRestartTimer();
      if (errorClearRef.current) window.clearTimeout(errorClearRef.current);
      isStartingRef.current = false;
      isListeningRef.current = false;
      if (recognitionRef.current === rec) {
        recognitionRef.current = null;
      }
      try {
        rec.stop();
      } catch {
        // Ignore invalid stop calls during cleanup.
      }
    };
  }, [lang]);

  const toggle = () => {
    if (!supported) return;
    if (isListeningRef.current || isStartingRef.current || listening) {
      stopRecognition();
    } else {
      stopRequested.current = false;
      startRecognition();
    }
  };

  const unsupportedLabel = labels.micNoSupportApp || labels.micNoSupport;

  return (
    <div className="intake-mic-wrap">
      <button
        type="button"
        className={`intake-mic-btn ${listening ? "active" : ""}`}
        onClick={toggle}
        disabled={!supported}
        title={supported ? labels.micStart : unsupportedLabel}
      >
        {supported ? (listening ? labels.micStop : labels.micStart) : unsupportedLabel}
      </button>
      {errorHint ? (
        <p className="intake-mic-error" role="status">
          {errorHint}
        </p>
      ) : null}
    </div>
  );
}

export default function Intake() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang, problemKey, params, location } = useQueryParams();
  const normalizedProblemKey = normalizeJourneyProblemKey(problemKey);
  const [intake, setIntake] = useState({
    painNow: "",
    desiredEndState: "",
    oneWord: "",
  });
  const [loaded, setLoaded] = useState(false);
  const [formError, setFormError] = useState("");
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summarySafety, setSummarySafety] = useState(false);

  useEffect(() => {
    if (problemKey && problemKey !== normalizedProblemKey) {
      const next = new URLSearchParams(params);
      next.set("problemKey", normalizedProblemKey);
      navigate(`/journey/intake?${next.toString()}`, { replace: true });
      return;
    }
    if (problemKey) return;
    navigate(`/journey/select?lang=${encodeURIComponent(lang)}`, { replace: true });
  }, [lang, navigate, normalizedProblemKey, params, problemKey]);

  useEffect(() => {
    if (!normalizedProblemKey) return;
    const { intake: existing } = JourneyService.loadIntake(lang, normalizedProblemKey);
    if (existing) {
      setIntake({
        painNow: existing.painNow || existing.painStory || "",
        desiredEndState: existing.desiredEndState || "",
        oneWord: existing.oneWord || existing.one_word || existing.oneWordNow || "",
      });
    }
    setLoaded(true);
  }, [lang, normalizedProblemKey]);

  const blueprint = normalizedProblemKey ? getBlueprintForProblem(normalizedProblemKey, lang) : null;
  const problemMeta = normalizedProblemKey ? getProblemByKey(normalizedProblemKey) : null;
  const problemTitle = normalizedProblemKey
    ? getProblemTitleForLang(normalizedProblemKey, lang, { includeDuration: false })
    : "";
  const totalDays = blueprint?.durationDays || blueprint?.totalDays || problemMeta?.defaultDays || 7;

  const onChangeField = (field, value) => {
    setIntake((prev) => ({ ...prev, [field]: value }));
  };

  const requestSummary = async () => {
    setSummarySafety(false);
    setSummary(null);
    setSummaryLoading(true);
    try {
      const userId = JourneyService.getOrCreateAiSessionId(lang, normalizedProblemKey);
      const res = await fetch(`${API_BASE}/api/ai/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userId,
          language: lang,
          problemKey: normalizedProblemKey,
          answers: [
            { questionId: "painNow", value: intake.painNow },
            { questionId: "desiredEndState", value: intake.desiredEndState },
            { questionId: "oneWord", value: intake.oneWord },
          ],
        }),
      });
      if (res.status === 451) {
        setSummarySafety(true);
        return;
      }
      const data = await res.json().catch(() => null);
      if (data?.ok) {
        setSummary(data.output || null);
      }
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    if (!intake.painNow.trim() || !intake.desiredEndState.trim() || !intake.oneWord.trim()) {
      setFormError(t.requiredError);
      return;
    }
    JourneyService.startNewJourney({
      lang,
      problemKey: normalizedProblemKey,
      intake: {
        ...intake,
        intakeSummary: summary?.summary || "",
        topNeeds: summary?.topNeeds || [],
        recommendedTone: summary?.recommendedTone || "",
        startingDayAdjustment: summary?.startingDayAdjustment || "",
        riskFlags: summary?.riskFlags || false,
        durationDays: totalDays,
      },
    });

    const p = new URLSearchParams(location.search);
    p.set("lang", lang);
    p.set("problemKey", normalizedProblemKey);
    navigate({
      pathname: "/journey/language-mirror",
      search: p.toString(),
    });
  };

  const dir = dirForLang(lang);
  const t = getSystemStrings(lang, user?.gender).intake;

  if (!loaded) return null;
  if (!normalizedProblemKey) return null;

  return (
    <div className="intake-page" dir={dir}>
      <section className="intake-header">
        <h1 className="intake-title">{t.headerTitle}</h1>
        <div className="intake-header-meta">
          <p className="intake-subtitle">{problemTitle}</p>
          <button
            type="button"
            className="intake-secondary intake-switch"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set("lang", lang);
              navigate(`/journey/explorer?${next.toString()}`);
            }}
          >
            {t.chooseAnotherProblem}
          </button>
        </div>
      </section>

      <form className="intake-form" onSubmit={handleSubmit}>
        <section className="intake-intro">
          <p className="intake-intro-line">{t.introLine1(problemTitle)}</p>
          <p className="intake-intro-line">{t.introLine2}</p>
        </section>

        <section className="intake-card">
          <h2 className="intake-card-title">{t.cardPainTitle}</h2>
          <p className="intake-card-subtitle">{t.cardPainDesc}</p>
          <div className="intake-field-with-mic">
            <SpeechButton
              lang={lang}
              labels={t}
              onText={(text) =>
                setIntake((prev) => ({
                  ...prev,
                  painNow: prev.painNow ? `${prev.painNow} ${text}` : text,
                }))
              }
            />
            <textarea
              className="intake-textarea"
              rows={3}
              value={intake.painNow}
              placeholder={t.cardPainPlaceholder}
              onChange={(e) => onChangeField("painNow", e.target.value)}
              required
            />
          </div>
        </section>

        <section className="intake-card">
          <h2 className="intake-card-title">{t.cardGoalTitle}</h2>
          <p className="intake-card-subtitle">{t.cardGoalDesc}</p>
          <div className="intake-field-with-mic">
            <SpeechButton
              lang={lang}
              labels={t}
              onText={(text) =>
                setIntake((prev) => ({
                  ...prev,
                  desiredEndState: prev.desiredEndState ? `${prev.desiredEndState} ${text}` : text,
                }))
              }
            />
            <textarea
              className="intake-textarea"
              rows={3}
              value={intake.desiredEndState}
              placeholder={t.cardGoalPlaceholder}
              onChange={(e) => onChangeField("desiredEndState", e.target.value)}
              required
            />
          </div>
        </section>

        <section className="intake-card">
          <h2 className="intake-card-title">{t.cardWordTitle}</h2>
          <p className="intake-card-subtitle">{t.cardWordDesc}</p>
          <div className="intake-field-with-mic">
            <SpeechButton
              lang={lang}
              labels={t}
              onText={(text) =>
                setIntake((prev) => ({
                  ...prev,
                  oneWord: prev.oneWord ? `${prev.oneWord} ${text}` : text,
                }))
              }
            />
            <input
              className="intake-input"
              type="text"
              value={intake.oneWord}
              placeholder={t.cardWordPlaceholder}
              onChange={(e) => onChangeField("oneWord", e.target.value)}
              required
            />
          </div>
        </section>

        <div className="intake-actions">
          <button type="submit" className="intake-submit-btn">
            {t.startJourney}
          </button>
          <button
            type="button"
            className="intake-secondary"
            onClick={requestSummary}
            disabled={summaryLoading}
          >
            {summaryLoading ? t.summaryLoading : t.summaryCta}
          </button>
        </div>
        {formError && <div className="intake-error">{formError}</div>}

        {summarySafety && (
          <div className="intake-summary">
            <div className="intake-summary-title">{t.summaryTitle}</div>
            <div className="intake-summary-line">{t.summarySafety}</div>
            <button
              type="button"
              className="intake-secondary"
              onClick={() => navigate(`/help-now?${params.toString()}`)}
            >
              {t.summarySafetyCta}
            </button>
          </div>
        )}

        {summary && !summarySafety && (
          <div className="intake-summary">
            <div className="intake-summary-title">{t.summaryTitle}</div>
            {summary.summary && <div className="intake-summary-line">{summary.summary}</div>}
            {Array.isArray(summary.topNeeds) && summary.topNeeds.length > 0 && (
              <div className="intake-summary-line">
                <span className="intake-summary-label">{t.summaryNeeds}</span>{" "}
                {summary.topNeeds.join(", ")}
              </div>
            )}
            {summary.startingNote && (
              <div className="intake-summary-line">
                <span className="intake-summary-label">{t.summaryStart}</span> {summary.startingNote}
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
}


